import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import CardMedia from "@mui/material/CardMedia";
import {
  resolveChannelLogoSrc,
  channelLogoSx,
  channelLogoChipFor,
  handleChannelLogoError,
  isOvLogoUrl,
  LegacyLogoErrorFallback
} from "lib/helper/channel-logo-url";

/**
 * WAT-11362 — one channel logo, in the white circular chip.
 *
 * The chip has to live on a CONTAINER: a border-radius on an `<img>` clips its content
 * (proven on NFL Network, which rendered "VETWORK" with the outer glyphs shaved). The
 * container also needs a DEFINITE box, because `channelLogoSx` sizes the image with
 * `width: 100%` and on a shrink-to-fit parent that resolves to the OV file's intrinsic
 * 120px (see the `/120/91` suffix on its url) instead of the design size.
 *
 * With the flag OFF the wrapper collapses to `display: contents` so it contributes no box
 * at all and the `<img>` stays the flex item — the flag-off render is production's.
 *
 * Extracted because this exact block appeared verbatim at 7 render sites; the 6px margin
 * and the size are calibration that has already needed tuning once.
 */
export interface ChannelLogoChipProps {
  /** Raw logo path — relative (legacy) or absolute (OV). Resolved internally. */
  src?: string;
  /** The server's decision from `pickChannelLogo`, carried on the channel. */
  isOvLogo?: boolean;
  alt?: string;
  /** Design size of the chip, e.g. "64px" or "80px". */
  size: string;
  /** `inline-flex` inside phrasing content (table cells), `flex` otherwise. */
  display?: "inline-flex" | "flex";
  /** Vertical breathing room restoring the transparent margin legacy artwork carried. */
  margin?: string;
  verticalAlign?: string;
  /** The render site's own image styles, applied before the shared treatment. */
  imgSx?: Record<string, unknown>;
  /** `span` when the parent only accepts phrasing content. */
  component?: "div" | "span";
  ariaLabelledBy?: string;
  testId?: string;
  /** What this site did on an image error BEFORE WAT-11362 — replayed when the flag is off. */
  legacyErrorFallback?: LegacyLogoErrorFallback;
}

const ChannelLogoChip = ({
  src,
  isOvLogo,
  alt,
  size,
  display = "flex",
  margin,
  verticalAlign,
  imgSx,
  component = "div",
  ariaLabelledBy,
  testId,
  legacyErrorFallback = "none"
}: ChannelLogoChipProps) => {
  // The server's decision wins whenever it survives the data path. It does not on the
  // compare tool (getFromCmsFeed strips unknown fields), so fall back to the url there
  // rather than silently drop the chip. An explicit `false` still wins.
  const isOv = isOvLogo ?? isOvLogoUrl(src);
  const flagOn = Boolean(isOv);
  return (
    <DtvBox
      component={component}
      sx={{
        display: flagOn ? display : "contents",
        ...(flagOn ? {
          width: size,
          height: size,
          ...(margin ? { margin } : {}),
          ...(verticalAlign ? { verticalAlign } : {})
        } : {}),
        ...channelLogoChipFor(isOv)
      }}
    >
      <CardMedia
        alt={alt}
        aria-labelledby={ariaLabelledBy}
        component="img"
        data-testid={testId}
        loading="lazy"
        src={resolveChannelLogoSrc(src)}
        sx={{
          ...(imgSx ?? {}),
          ...channelLogoSx(isOv)
        }}
        onError={e => handleChannelLogoError(e, isOv, legacyErrorFallback)}
      />
    </DtvBox>
  );
};

export default React.memo(ChannelLogoChip);
